import { isMobile } from "mobile-device-detect";
import { Settings } from "@/constant";
import { useBreakpoints, createSharedComposable } from '@vueuse/core';

export const getTransformMatrix = (myElement) => {
  const MatrixAPI = window["DomMatrix"] || window.WebKitCSSMatrix;
  const currentMatrix =
    getComputedStyle(myElement).getPropertyValue("transform");
  return new MatrixAPI(currentMatrix);
};

export const tranformOriginOfElement = (element, x, y) =>{
  const vendors = [
    "-webkit-transform",
    "-moz-transform",
    "-ms-transform",
    "-o-transform",
    "transform",
  ];
  for (const vendor of vendors) {
    element.style[vendor] = `scale(${x}, ${y})`;
  }
};

// A plat SVG without a viewBox has no intrinsic coordinate system to scale, so
// the drawing is clipped to the crop box instead of being fitted into it. Derive
// a viewBox from the rendered geometry so the whole plat always has one.
const ensureViewBox = (svg: SVGSVGElement) => {
  const viewBox = svg.viewBox?.baseVal;
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) return;

  try {
    const bounds = svg.getBBox();
    if (bounds.width > 0 && bounds.height > 0) {
      svg.setAttribute(
        "viewBox",
        `${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`
      );
    }
  } catch {
    // getBBox() throws while the clone is still unrendered - nothing to fit yet.
  }
};

// `fitWholePlat` scales the entire plat into the crop box instead of letting the
// box clip it. Only sales/land opt in: customer mode prints correctly today
// because its crop box (full browser window) is large enough to contain the plat
// at natural size, and it must keep the original code path untouched.
export const fitSVGToModal = (defaultScale: number, fitWholePlat = false) => {
  const div = document.getElementById("crop");
  const svg = div?.getElementsByTagName("svg")[0];
  if (!div || !svg) return;

  if (fitWholePlat) {
    // Dragging the preview leaves an inline offset behind; without clearing it
    // the next print starts panned and loses whatever sits outside the crop box.
    div.style.top = "0px";
    div.style.left = "0px";
  }

  svg.setAttribute("width", div.clientWidth + "px");
  svg.setAttribute("height", div.clientHeight + "px");
  svg.setAttribute("style", ``);
  tranformOriginOfElement(svg, defaultScale, defaultScale);

  if (!fitWholePlat) {
    svg.setAttribute("preserveaspectratio", "xMidYMid meet");
    return;
  }

  ensureViewBox(svg);

  // The crop box almost never matches the plat's aspect ratio, so the fit relies
  // entirely on preserveAspectRatio. SVG attribute names are case-sensitive: the
  // all-lowercase spelling above is inert, which leaves the source plat's own
  // value in charge - and a "slice" value crops the map instead of scaling it.
  svg.removeAttribute("preserveaspectratio");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
}

export const getPositiveValue = (val) => {
  const item = val < 0 ? val * -1 : val;
  return item;
};

export const normalZoomIn = (svgZoom, zoomStep) => {
  const zoomParams = {
    isDisabledZoomIn: false,
    isDisabledZoomOut: false,
    svgZoom
  }
  let newSvgZoom = svgZoom + zoomStep;

  const mapMaxValue = isMobile
    ? Settings.MOBILE_MAX_SVG_SCALE
    : Settings.WEB_MAX_SVG_SCALE;
  const mapMinValue = 1;
  if (newSvgZoom >= mapMaxValue) {
    zoomParams.isDisabledZoomIn = true;
    newSvgZoom = mapMaxValue;
  } else {
    zoomParams.isDisabledZoomIn = false;
  }
  if (newSvgZoom <= mapMinValue) {
    zoomParams.isDisabledZoomOut = true;
    newSvgZoom = mapMinValue;
  } else {
    zoomParams.isDisabledZoomOut = false;
  }
  zoomParams.svgZoom = newSvgZoom;

  return zoomParams;
};

export const closeDragElement = () => {
  if (isMobile) {
    document.ontouchend = null;
    document.ontouchmove = null;
  } else {
    document.onmouseup = null;
    document.onmousemove = null;
  }
}

export const useScreenSize = createSharedComposable(() => {
  const screen = useBreakpoints({
    '2xs': 320,
    xs: 375,
    sm: 414,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1366,
    '3xl': 1440,
    '4xl': 1920,
  });

  const is2Xs = screen.greaterOrEqual('2xs');
  const isXs = screen.greaterOrEqual('xs');
  const isSm = screen.greaterOrEqual('sm');
  const isMd = screen.greaterOrEqual('md');
  const isLg = screen.greaterOrEqual('lg');
  const isXl = screen.greaterOrEqual('xl');
  const is2Xl = screen.greaterOrEqual('2xl');
  const is3Xl = screen.greaterOrEqual('3xl');
  const is4Xl = screen.greaterOrEqual('4xl');

  return {
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2Xl,
    is3Xl,
    is4Xl,
  };
});